// One place to turn an axios rejection into a string React can render.
//
// `main.py`'s RequestValidationError handler returns `{"detail": exc.errors()}`
// -- an ARRAY of `{loc, msg, type}` objects, not a string. Reading
// `err.response.data.detail` and dropping it straight into state therefore
// puts objects into JSX, which throws "Objects are not valid as a React
// child". Nothing in App.js is an ErrorBoundary, so React 18 unmounts the
// whole root: the mechanic gets a white screen and loses what they typed.
//
// The reachable case is the ordinary one -- typing `16` for a 2016 Civic.
// The client guard used to check field PRESENCE only, while the server
// enforces `year: ge=1900, le=2030` (inquiry_models.py). Hence both this
// coercion and the mirrored bounds in inquiryFields.js.

// Pull the field name out of a FastAPI `loc`, e.g. ["body", "year"] -> "year".
const fieldFromLoc = (loc) => {
  if (!Array.isArray(loc)) return null;
  const named = loc.filter((part) => typeof part === 'string' && part !== 'body');
  return named.length > 0 ? named[named.length - 1] : null;
};

/**
 * Coerce whatever the server put in `detail` into a renderable string.
 *
 * - a plain string (our own 400/409 messages) is returned verbatim, because
 *   those are written for the mechanic and say which job conflicts;
 * - an array of FastAPI validation errors is joined into "field: message"
 *   pairs, so "year: Input should be greater than or equal to 1900" is
 *   something the user can act on;
 * - anything else (a network error with no `response`, an object, a number)
 *   falls back to the caller's generic message.
 */
export const messageFromError = (err, fallback) => {
  const detail = err?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) return detail.trim();

  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (!item || typeof item.msg !== 'string') return null;
        const field = fieldFromLoc(item.loc);
        return field ? `${field}: ${item.msg}` : item.msg;
      })
      .filter(Boolean);
    if (parts.length > 0) return parts.join('; ');
  }

  return fallback;
};

export default messageFromError;
